import React, { useCallback, useRef, useState } from 'react';
import { useOutsideClick } from 'rooks';
import { ButtonSizes, PrimaryButton } from 'src/shared/design-system/Buttons';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'src/shared/design-system/Modal';
import { Paragraph, Span } from 'src/shared/design-system/Text';
import { InputType } from 'src/shared/generic-components/AppInput';
import { viewHeightCSS } from 'src/shared/utils/view-height-fix';
import { ZIndexes } from 'src/shared/ZIndexes';
import styled from 'styled-components';
import { useScreenContext } from 'web-common/src/shared/context/ScreenContext/hook';
import { Colors, Font, ScreenWidth, Spacing } from 'web-common/src/shared/styles';

export const DropdownContainer = styled.div`
  position: absolute;
  z-index: ${ZIndexes.DropdownFilter};
  border: 1px solid ${Colors.GRAY_LITE_10};
  border-top: none;
  border-radius: 20px;
  box-shadow: 15px 15px 30px 5px rgba(0, 0, 0, 0.1);
  background: ${Colors.WHITE};
  user-select: none;
  padding: 30px;
  width: 300px;
  right: 0;

  @media screen and (max-width: ${ScreenWidth.SMALL}) {
    bottom: 0;
    width: 100%;
    ${viewHeightCSS(80)}
  }
`;

const Title = styled(Paragraph)`
  font-size: 24px;
  font-weight: 400;
  margin: 0;

  @media screen and (max-width: ${ScreenWidth.SMALL}) {
    font-size: 32px;
    padding: 45px 20px 0;
  }
`;

const OptionWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  cursor: pointer;
  padding: 10px 0;
  background-color: ${Colors.WHITE};
  background-clip: border-box;
  border-radius: 0.25rem;
  min-width: fit-content;

  :first-child {
    padding-top: 1px;
  }

  :last-child {
    padding-bottom: 0;
  }

  .checkbox {
    height: 20px;
    width: 20px;
    cursor: pointer;
    margin: 0;
  }
`;

const OptionWrapperContainer = styled.div`
  max-height: 350px;
  overflow-y: auto;
  overscroll-behavior: contain;
  margin: 20px 0;

  -ms-overflow-style: none;
  scrollbar-width: none;
  & ::-webkit-scrollbar {
    display: none;
  }

  @media screen and (max-width: ${ScreenWidth.SMALL}) {
    max-height: 100%;
    padding: 0 20px;
    margin: 0;
  }
`;

const FilterSelectorModalSaveButton = styled(PrimaryButton)`
  width: 140px;
  height: 45px;
  font-size: 18px;
`;

const ApplyButton = styled(PrimaryButton)`
  padding: 10px 30px !important;
`;

export interface Option<T extends string = string> {
  value: T;
  display: JSX.Element;
}

interface IProps<T extends string> {
  filterTitle: string;
  filterKey: string;
  singleFilter?: boolean;

  filterOptions: Option<T>[];
  selectedValues: T[];
  onSubmit: (values?: T[]) => void;
}

export const FilterSelector = <T extends string>({
  filterKey,
  filterTitle,
  filterOptions,
  selectedValues,
  singleFilter,
  onSubmit,
}: IProps<T>): JSX.Element => {
  const { isSmallScreen } = useScreenContext();
  const wrapperRef = useRef() as React.MutableRefObject<HTMLDivElement>;
  const [selectedOptions, setSelectedOptions] = useState<T[]>(selectedValues);

  useOutsideClick(wrapperRef, () => onSubmit());

  const handleOptionChange = useCallback(
    (selectedOption: T) => {
      const includedOptions = singleFilter ? [] : [...selectedOptions];
      if (selectedOptions.some((option: T) => option === selectedOption)) {
        return setSelectedOptions([...includedOptions.filter((option: string) => option !== selectedOption)]);
      }

      return setSelectedOptions([...includedOptions, selectedOption]);
    },
    [selectedOptions, singleFilter]
  );

  const handleSubmit = useCallback(() => {
    // Submitting undefined doesn't change filters but closes selector. Submitting empty array clears the filter
    const submitValues = filterOptions.length ? selectedOptions.map((selectedOption) => selectedOption) : undefined;
    onSubmit(submitValues);
  }, [selectedOptions, filterOptions, onSubmit]);

  const handleClose = useCallback(() => {
    onSubmit();
  }, [onSubmit]);

  const title = <Title>{filterTitle}</Title>;

  const filterChildren = (
    <OptionWrapperContainer key={filterKey}>
      {filterOptions.map((option) => (
        <OptionWrapper
          key={option.value}
          data-cy={`${filterKey}-filter-option`}
          onClick={() => handleOptionChange(option.value)}>
          <Span
            mobileSize={Font.Size.M}
            size={Font.Size.S}
            margin={Spacing.NONE}
            weight={Font.Weight.MEDIUM}
            fontFamily={Font.Family.BODY_SANS}>
            {option.display}
          </Span>
          <input
            className='checkbox'
            type={InputType.CHECKBOX}
            checked={Boolean(selectedOptions.find((checkedOption) => checkedOption === option.value))}
            onClick={(e) => e.stopPropagation()}
            onChange={() => handleOptionChange(option.value)}
          />
        </OptionWrapper>
      ))}
    </OptionWrapperContainer>
  );

  return (
    <>
      {isSmallScreen ? (
        <Modal
          isShowing
          blockScrollOnMount
          showCloseButton
          isFullWidth
          isFullHeight
          animation='fade'
          onClose={handleClose}>
          <ModalHeader>{title}</ModalHeader>
          <ModalBody>{filterChildren}</ModalBody>
          <ModalFooter>
            <FilterSelectorModalSaveButton onClick={handleSubmit}>Save</FilterSelectorModalSaveButton>
          </ModalFooter>
        </Modal>
      ) : (
        <DropdownContainer ref={wrapperRef}>
          {title}
          {filterChildren}
          <ApplyButton size={ButtonSizes.S} onClick={handleSubmit} name='filter-selection-submit-button'>
            Apply
          </ApplyButton>
        </DropdownContainer>
      )}
    </>
  );
};
